import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { WidgetComponent } from '../widget/widget.component';
import { wrapGrid } from 'animate-css-grid';
import { Chart } from 'chart.js';
import { Dashboard, Widget } from '../models/dashboard';
import { DialogService } from 'primeng/dynamicdialog';
import { DashboardFactory } from '../factory/dashboard.factory';
import { ActivatedRoute } from '@angular/router';
import { CdkDragEnd, CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import { ResizableModule, ResizeEvent } from 'angular-resizable-element';
import { DashboardService } from '../services/dashboard.service';
import { CreateOrUpdateDashboardDialogComponent } from '../create-or-update-dashboard-dialog/create-or-update-dashboard-dialog.component';

@Component({
  selector: 'app-update-dashboard',
  providers: [HttpClient],
  standalone: true,
  imports: [
    WidgetComponent,      // Eigenes Widget-Child-Component
    Button,               // PrimeNG Button
    MenuModule,           // PrimeNG Menü
    MenubarModule,        // PrimeNG Menubar
    DragDropModule,       // Angular CDK Drag & Drop
    FormsModule,          // NgModel, ngForm etc.
    CommonModule,         // ngIf, ngFor, …
    ResizableModule       // angular-resizable-element
  ],
  templateUrl: './update-dashboard.component.html',
  styleUrl: './update-dashboard.component.css'
})
export class UpdateDashboardComponent {
  // ActivatedRoute injizieren, um Route-Resolver-Daten zu erhalten
  route = inject(ActivatedRoute);
  // DialogService injizieren, um Dialoge (z.B. Erstell-/Bearbeitungsdialog) zu öffnen
  dialogService = inject(DialogService);
  // DashboardService injizieren, der alle Layout-Funktionen kapselt
  dashboardService = inject(DashboardService);

  // ViewChild, um eine Referenz auf das Dashboard-Element im DOM zu bekommen
  dashboardElementRef = viewChild.required<ElementRef>('dashboard');

  // Signal, das den aktuell geladenen Dashboard-Datensatz hält
  dashboard: WritableSignal<Dashboard> = signal(DashboardFactory.createEmpty());
  // Platzhalter zur Anzeige der Drag-Preview-Box (Overlay im Grid)
  dragPreview: Partial<Widget & { visible: boolean }> = {
    visible: false,
    columnPosition: 1,
    rowPosition: 1,
  };

  // Maximale Spaltenanzahl im Grid
  private readonly columnsCount = 4;
  // Basis-Höhe einer Zeile in Pixeln (150px) ohne gap
  private readonly baseRowHeight = 150;

  // Flag, um Benutzerrechte z.B. auf Admin-Ebene darzustellen
  public isAdmin = true;
  // Die berechnete Breite einer Spalte in Pixeln (wird in ngAfterViewInit gesetzt)
  public colWidth!: number;
  // Die berechnete Höhe einer Zeile inkl. gap in Pixeln
  public rowHeight!: number;
  // Der Gap-Wert zwischen den Grid-Zellen (wird in ngAfterViewInit gesetzt)
  public gridGap!: number;

  // Funktion, um die animate-css-grid-Animation später wieder zu deaktivieren
  clearAnimations = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    // Setzt das DashboardService intern zurück (löscht ggf. alle Widgets)
    this.dashboardService.reset();

    // Lese die Daten aus dem Route-Resolver aus (DashboardResolver liefert 'dashboard')
    this.route.data.subscribe(data => {
      // Setze das Dashboard-Signal auf das vom Resolver gelieferte Objekt
      this.dashboard.set(data['dashboard']);
      // Initialisiere die Widgets im Service anhand des geladenen Dashboards
      this.dashboardService.addedWidgets.set(this.dashboard().widgets);
    });

    // wrapGrid um das Dashboard-Element, um Animate-CSS-Grid zu aktivieren
    const { unwrapGrid } = wrapGrid(this.dashboardElementRef().nativeElement, {
      duration: 300, // Animationsdauer in ms
    });
    // Speichere die Funktion zum späteren Ausschalten der Animation
    this.clearAnimations = unwrapGrid;

    // Setze Standard-Farbe für Chart.js
    Chart.defaults.color = 'white';
  }

  ngAfterViewInit(): void {
    // Berechne hier einmalig die Grid-Abmessungen (colWidth, rowHeight, gridGap)
    this.calculateGridSizes();
  }

  ngOnDestroy() {
    // Wenn die Komponente zerstört wird, schalte die CSS-Grid-Animation ab
    this.clearAnimations();
  }

  /**
   * Öffnet einen Dialog, um das Dashboard zu aktualisieren.
   * Dabei werden die Widgets aus dem Service ins Dashboard-Objekt geschrieben
   * und als Daten ins Dialog-Component übergeben.
   */
  updateDashboard() {
    // Schreibe die aktuell im Service gespeicherten Widgets ins Dashboard-Signal
    this.dashboard().widgets = this.dashboardService.addedWidgets();
    // Öffne den Create/Update Dialog und übergebe das Dashboard-Signal
    this.dialogService.open(CreateOrUpdateDashboardDialogComponent, {
      header: 'Dashboard aktualisieren',
      width: '60%',
      modal: true,
      data: {
        dashboard: this.dashboard,
        isNew: false
      }
    });
  }

  /**
   * Wird aufgerufen, wenn ein Benutzer beginnt, ein Widget zu ziehen.
   * Wir schalten die CSS-Grid-Animation ab, damit das Grid während des Draggens
   * nicht flackert.
   */
  onDragStarted() {
    this.clearAnimations();
  }

  /**
   * Wird bei jedem Mausmove während des Ziehens ausgelöst.
   * Berechnet anhand des Drag-Events und des Grid-Layouts die neue Spalte/Zeile
   * für die Drag-Preview-Box und zeigt das Overlay im Zielbereich an.
   *
   * @param event  - CdkDragMove-Event mit aktuellen Maus-/Element-Koordinaten
   * @param widget - Das Widget, das gerade gezogen wird
   */
  onDragMoved(event: CdkDragMove, widget: Widget) {
    // Berechne gerasterte Spalte/Zeile
    const { newCol, newRow } = this.dashboardService.calculateGridPosition(
      event,
      widget,
      this.dashboardElementRef,
      this.gridGap,
      this.columnsCount,
      this.rowHeight
    );

    // Aktualisiere das dragPreview-Objekt, um das Overlay sichtbar zu machen
    Object.assign(this.dragPreview, {
      visible: true,
      columnPosition: newCol,
      rowPosition: newRow,
      columnSpan: widget.columnSpan,
      rowSpan: widget.rowSpan,
    });
  }

  /**
   * Wird ausgelöst, wenn der Benutzer ein Widget fallen lässt (DragEnd).
   * Aktualisiert die tatsächliche Widget-Position, reaktiviert die Grid-Animation
   * und löst Kollisionen auf.
   *
   * @param event  - CdkDragEnd-Event
   * @param widget - Das Widget, das soeben losgelassen wurde
   */
  onDragEnded(event: CdkDragEnd, widget: Widget) {
    // Grid-Animation erneut wrappen (damit weitere Änderungen wieder animiert werden)
    const { unwrapGrid } = wrapGrid(this.dashboardElementRef().nativeElement, {
      duration: 300,
    });
    this.clearAnimations = unwrapGrid;

    // Berechne finale Spalten-/Zeilenkoordinaten fürs Loslassen
    const { newCol, newRow } = this.dashboardService.calculateGridPosition(
      event,
      widget,
      this.dashboardElementRef,
      this.gridGap,
      this.columnsCount,
      this.rowHeight
    );

    // Verstecke die Preview-Box im Grid
    this.dragPreview.visible = false;

    // Tatsächliche Position erst jetzt im Widget setzen
    widget.columnPosition = newCol;
    widget.rowPosition = newRow;

    // Reset des Drag-Transforms, damit das Widget per CSS-Grid einsnappt
    event.source.reset();

    // Kollisionen mit anderen Widgets auflösen
    this.dashboardService.resolveCollisions(widget);
    // Widget-Daten im Service updaten (z.B. um später zu speichern)
    this.dashboardService.updateWidget(widget.widgetId, widget);
  }

  /**
   * Wird ausgelöst, wenn der Benutzer ein Widget via Resize-Handle vergrößert/verkleinert.
   * Berechnet die neuen columnSpan/rowSpan anhand der Pixelmaße und löst dann Kollisionen.
   *
   * @param event  - ResizeEvent mit neuer Rechteck-Größe
   * @param widget - Das Widget, das gerade resized wurde
   */
  onResizeEnd(event: ResizeEvent, widget: Widget) {
    // Ermittle das Grid-Element und seine Breite
    const gridRect = this.dashboardElementRef().nativeElement.getBoundingClientRect();
    // Spaltenbreite in Pixeln (nachdem ColCount=4 bekannt ist)
    const colWidth = gridRect.width / 4;

    // Neue Spalten- und Zeilenanzahl anhand der gerasteten Pixelwerte
    const newColSpan = Math.max(1, Math.round((event.rectangle?.width ?? 0) / colWidth));
    const newRowSpan = Math.max(1, Math.round((event.rectangle?.height ?? 0) / 150));

    widget.columnSpan = newColSpan;
    widget.rowSpan = newRowSpan;

    // Kollisionen nach der neuen Größe auflösen
    this.dashboardService.resolveCollisions(widget);
  }

  /**
   * Berechnet einmalig die Breite einer Spalte (colWidth), die Höhe einer Zeile (rowHeight)
   * und den gap-Wert, nachdem die View initialisiert ist. Diese Werte werden für Drag/Resize
   * benötigt, um exaktes Rastern zu ermöglichen.
   */
  private calculateGridSizes() {
    // Hole das Grid-Element (dashboardElementRef) und dessen CSS-Stile
    const gridEl = this.dashboardElementRef().nativeElement;
    const style = getComputedStyle(gridEl);
    const rect = gridEl.getBoundingClientRect();

    // Lese Padding links/rechts aus (z.B. 1rem = 16px)
    const padL = parseFloat(style.paddingLeft) || 0;
    const padR = parseFloat(style.paddingRight) || 0;
    // Lese gap (columnGap/rowGap) aus (z.B. Tailwind-Gap von 16px)
    this.gridGap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;

    // Berechne nutzbare Gesamtbreite (rect.width minus Padding)
    const totalW = rect.width - padL - padR;
    // Ziehe alle Lücken (gaps) ab: (columnsCount - 1) × gap
    const totalGap = this.gridGap * (this.columnsCount - 1);

    // Spaltenbreite = (nutzbare Breite − alle gaps) ÷ columnsCount
    this.colWidth = (totalW - totalGap) / this.columnsCount;
    // Zeilenhöhe = basis 150px plus gap
    this.rowHeight = this.baseRowHeight + this.gridGap;

    // Fordere Angular auf, das Template erneut zu prüfen (ChangeDetection),
    // damit alle Bindings (z.B. dragPreview-Berechnungen) mit den neuen Werten laufen.
    this.cdr.detectChanges();
  }

}
